/**
 * Releases, pins and each node's release (container, D15; the rollout is the console's Rollouts section, D40), and the client releases calling the API (D37): the panels, the pin dialog
 * (stage or site), and clearing a pin. Live through `releasePin` changes and
 * the vendor mirror (a node reporting new packages moves the rollout).
 */
import { useState } from 'react';
import { toast } from 'sonner';
import type { PinTarget } from '../api/vendor.js';
import { useChangePin, useClearRejection, useVendorClients, useVendorReleases } from '../app/hooks.js';
import { ClientReleasesPanel } from '../components/vendor/ClientReleasesPanel.js';
import { PinDialog, type PinDialogTarget } from '../components/vendor/PinDialog.js';
import { ReleasesPanel } from '../components/vendor/ReleasesPanel.js';
import { useVendorAdmin } from './VendorLayout.js';

function messageOf(err: unknown): string {
  return err instanceof Error ? err.message : String(err);
}

function targetText(target: PinTarget): string {
  return target.kind === 'stage' ? 'the stage' : `site ${target.tenantId}/${target.siteId}`;
}

/** The client-release view's window: the SPA's 14 days fit, and a month of CLI use shows (D37 §4.1). */
const CLIENT_DAYS = 30;

export function VendorReleasesView() {
  const releases = useVendorReleases();
  const clients = useVendorClients(CLIENT_DAYS);
  const change = useChangePin();
  const clearRejection = useClearRejection();
  const canAdmin = useVendorAdmin();
  const [pinning, setPinning] = useState<PinDialogTarget | null>(null);
  const data = releases.data ?? null;
  const current =
    pinning === null || data === null
      ? null
      : pinning.kind === 'stage'
        ? (data.stagePin?.listHash ?? null)
        : (data.sitePins.find((p) => p.tenantId === pinning.tenantId && p.siteId === pinning.siteId)?.pin.listHash ?? null);
  const clear = (target: PinTarget) =>
    change.mutate(
      { target, action: { kind: 'clear' } },
      {
        onSuccess: () => toast.success(`Cleared the pin of ${targetText(target)}`),
        onError: (err) => toast.error(`Not cleared: ${messageOf(err)}`),
      },
    );
  return (
    <>
      <ReleasesPanel
        releases={data}
        loading={releases.isFetching}
        error={releases.error === null ? null : messageOf(releases.error)}
        canAdmin={canAdmin}
        onRetry={() => void releases.refetch()}
        onPinStage={() => {
          change.reset();
          setPinning({ kind: 'stage' });
        }}
        onPinSite={(tenantId, siteId) => {
          change.reset();
          setPinning({ kind: 'site', tenantId, siteId });
        }}
        onClearStage={() => clear({ kind: 'stage' })}
        onClearSite={(tenantId, siteId) => clear({ kind: 'site', tenantId, siteId })}
        onClearRejection={(target) =>
          clearRejection.mutate(target, {
            onSuccess: () => toast.success(`Cleared: node ${target.nodeId} may try that release again`),
            onError: (err) => toast.error(`Not cleared: ${messageOf(err)}`),
          })
        }
      />
      <ClientReleasesPanel clients={clients.data ?? null} loading={clients.isFetching} error={clients.error === null ? null : messageOf(clients.error)} onRetry={() => void clients.refetch()} />
      <PinDialog
        target={pinning}
        releases={data?.releases ?? []}
        current={current}
        pending={change.isPending}
        error={change.error === null ? null : messageOf(change.error)}
        onClose={() => setPinning(null)}
        onSubmit={({ target, listHash, note }) =>
          change.mutate(
            { target, action: { kind: 'set', listHash, note } },
            {
              onSuccess: () => {
                setPinning(null);
                toast.success(`Pinned ${targetText(target)}`);
              },
            },
          )
        }
      />
    </>
  );
}
